import { useState, useEffect, useMemo } from "react";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, TrendingDown, Clock, Brain, Users, CheckCircle2, HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface ConflictSummary {
  strategyA?: string;
  strategyB?: string;
}

interface Phase12EscalateInputProps {
  onValueChange: (value: string) => void;
  onStructuredChange?: (data: {
    credibility: string;
    speed: string;
    learningLoss: string;
    resistance: string;
    additionalCost?: string;
  }) => void;
  previousConflict?: ConflictSummary;
  className?: string;
}

interface CostCategory {
  key: 'credibility' | 'speed' | 'learningLoss' | 'resistance';
  label: string;
  question: string;
  icon: React.ElementType;
}

const costCategories: CostCategory[] = [
  { 
    key: 'credibility', 
    label: 'Außenwirkung',
    question: 'Schadet Zögern deiner Glaubwürdigkeit bei Kunden oder Partnern?',
    icon: TrendingDown
  },
  { 
    key: 'speed', 
    label: 'Zeitverlust',
    question: 'Wie viel Zeit geht durch Nicht-Entscheiden verloren?',
    icon: Clock
  },
  { 
    key: 'learningLoss', 
    label: 'Lernverlust',
    question: 'Welche wichtigen Erkenntnisse verpasst du ohne Aktion?',
    icon: Brain
  },
  { 
    key: 'resistance', 
    label: 'Team-Blockade',
    question: 'Wie stark lähmt die Unklarheit dein Team?',
    icon: Users
  },
];

const costOptions = [
  { value: "high", label: "Hoch", color: "text-rose-700 dark:text-rose-300", bgActive: "bg-rose-500/20 border-rose-500 ring-2 ring-rose-500/30" },
  { value: "medium", label: "Mittel", color: "text-amber-700 dark:text-amber-300", bgActive: "bg-amber-500/20 border-amber-500" },
  { value: "low", label: "Gering", color: "text-emerald-700 dark:text-emerald-300", bgActive: "bg-emerald-500/20 border-emerald-500" },
];

export const Phase12EscalateInput = ({
  onValueChange,
  onStructuredChange,
  previousConflict,
  className
}: Phase12EscalateInputProps) => {
  const [costs, setCosts] = useState<Record<string, string>>({
    credibility: '',
    speed: '',
    learningLoss: '',
    resistance: ''
  });
  const [additionalCost, setAdditionalCost] = useState("");

  const updateCost = (key: string, value: string) => {
    setCosts(prev => ({ ...prev, [key]: value }));
  };

  // Combine values
  useEffect(() => {
    const parts: string[] = [];
    if (costs.credibility) parts.push(`Glaubwürdigkeit: ${costs.credibility}`);
    if (costs.speed) parts.push(`Geschwindigkeit: ${costs.speed}`);
    if (costs.learningLoss) parts.push(`Lernverlust: ${costs.learningLoss}`);
    if (costs.resistance) parts.push(`Interner Widerstand: ${costs.resistance}`);
    if (additionalCost.trim()) parts.push(`Zusätzlich: ${additionalCost.trim()}`);

    if (parts.length > 0) {
      onValueChange(parts.join("\n"));
      onStructuredChange?.({
        credibility: costs.credibility,
        speed: costs.speed,
        learningLoss: costs.learningLoss,
        resistance: costs.resistance,
        additionalCost: additionalCost.trim()
      });
    } else {
      onValueChange("");
    }
  }, [costs, additionalCost, onValueChange, onStructuredChange]);

  // Check if at least one "high" is selected
  const hasHighCost = useMemo(() => {
    return Object.values(costs).some(v => v === 'high');
  }, [costs]);

  const filledCount = useMemo(() => {
    return Object.values(costs).filter(v => v !== '').length;
  }, [costs]);

  const allFilled = filledCount === 4;

  // Calculate pain score for visual feedback
  const painScore = useMemo(() => {
    let score = 0;
    Object.values(costs).forEach(v => {
      if (v === 'high') score += 3;
      if (v === 'medium') score += 1;
    });
    return score;
  }, [costs]);

  return (
    <div className={cn("space-y-5", className)}>
      {/* Scharfe Führung */}
      <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold text-foreground">
            Kosten der Nicht-Entscheidung
          </h3>
          <p className="text-[15px] text-foreground/80 max-w-lg mx-auto leading-relaxed">
            Bewerte, wie sehr dich <span className="font-medium text-foreground">Abwarten</span> in diesen vier Bereichen schadet.
          </p>
          <p className="text-sm text-muted-foreground">
            Wenn nichts weh tut, ist es keine echte Entscheidung.
          </p>
        </div>
      </div>

      {/* Conflict context reference */}
      {previousConflict?.strategyB && (
        <div className="flex items-start gap-3 py-3 px-4 bg-rose-500/10 border border-rose-500/30 rounded-xl">
          <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-rose-700 dark:text-rose-300">
              Identifiziertes Risiko (Option B)
            </p>
            <p className="text-sm text-foreground mt-0.5">
              {previousConflict.strategyB}
            </p>
          </div>
        </div>
      )}

      {/* Cost Assessment Grid - responsive */}
      <div className="grid gap-4 sm:grid-cols-2">
        {costCategories.map((category) => {
          const Icon = category.icon;
          const isSelected = costs[category.key] !== '';
          const isHigh = costs[category.key] === 'high';
          
          return (
            <div 
              key={category.key} 
              className={cn(
                "p-4 rounded-xl border-2 transition-all",
                isHigh 
                  ? "border-rose-500/50 bg-rose-500/5" 
                  : isSelected 
                    ? "border-border bg-background shadow-sm" 
                    : "border-border/50 bg-muted/10 hover:border-border"
              )}
            >
              <div className="space-y-3">
                {/* Category Header */}
                <div className="flex items-center gap-2">
                  <div className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center",
                    isHigh 
                      ? "bg-rose-500/20" 
                      : isSelected 
                        ? "bg-primary/10" 
                        : "bg-muted"
                  )}>
                    <Icon className={cn(
                      "w-4 h-4",
                      isHigh 
                        ? "text-rose-600 dark:text-rose-400" 
                        : isSelected 
                          ? "text-primary" 
                          : "text-muted-foreground"
                    )} />
                  </div>
                  <Label className="text-sm font-semibold text-foreground">
                    {category.label}
                  </Label>
                </div>
                
                {/* Question */}
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {category.question}
                </p>
                
                {/* Rating Buttons */}
                <RadioGroup
                  value={costs[category.key]}
                  onValueChange={(value) => updateCost(category.key, value)}
                  className="flex gap-2"
                >
                  {costOptions.map((option) => (
                    <label
                      key={option.value}
                      className={cn(
                        "flex-1 flex items-center justify-center py-2.5 rounded-lg border-2 cursor-pointer transition-all text-sm font-medium",
                        costs[category.key] === option.value
                          ? option.bgActive
                          : "bg-background border-border/50 text-muted-foreground hover:border-border hover:bg-muted/30"
                      )}
                    >
                      <RadioGroupItem value={option.value} className="sr-only" />
                      <span className={costs[category.key] === option.value ? option.color : ""}>
                        {option.label}
                      </span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress indicator */}
      {filledCount > 0 && filledCount < 4 && (
        <div className="flex items-center justify-center gap-2">
          <div className="flex gap-1">
            {[1, 2, 3, 4].map((i) => (
              <div 
                key={i} 
                className={cn(
                  "w-2 h-2 rounded-full transition-colors",
                  i <= filledCount ? "bg-primary" : "bg-border"
                )}
              />
            ))}
          </div>
          <span className="text-sm text-muted-foreground">
            {filledCount} von 4 bewertet
          </span>
        </div>
      )}

      {/* Warning if no high cost */}
      {allFilled && !hasHighCost && (
        <div className="p-4 rounded-xl border-2 border-rose-500 bg-rose-500/10 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-rose-600 dark:text-rose-400 flex-shrink-0" />
            <div>
              <p className="text-base font-semibold text-rose-700 dark:text-rose-300">
                Keine echte Entscheidung erkennbar
              </p>
              <p className="text-sm text-rose-600/90 dark:text-rose-400/90 mt-1 leading-relaxed">
                Wenn Nicht-Entscheiden in keinem Bereich wirklich weh tut, 
                ist es vielleicht gar keine dringende Entscheidung. 
                Überprüfe, ob mindestens ein Bereich <strong>hoch</strong> sein sollte.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Success indicator when pain is high */}
      {allFilled && hasHighCost && (
        <div className={cn(
          "p-4 rounded-xl border-2 animate-in fade-in slide-in-from-bottom-2 duration-300",
          painScore >= 6 
            ? "border-emerald-500/50 bg-emerald-500/10" 
            : "border-amber-500/50 bg-amber-500/10"
        )}>
          <div className="flex items-start gap-3">
            <CheckCircle2 className={cn(
              "w-5 h-5 flex-shrink-0",
              painScore >= 6 
                ? "text-emerald-600 dark:text-emerald-400" 
                : "text-amber-600 dark:text-amber-400"
            )} />
            <div>
              <p className={cn(
                "text-base font-semibold",
                painScore >= 6 
                  ? "text-emerald-700 dark:text-emerald-300" 
                  : "text-amber-700 dark:text-amber-300"
              )}>
                {painScore >= 6 
                  ? "Klare Kosten erkannt" 
                  : "Kosten erkannt"
                }
              </p>
              <p className="text-sm text-muted-foreground mt-0.5">
                {painScore >= 6 
                  ? "Es gibt echten Handlungsdruck – gut so." 
                  : "Es gibt Handlungsdruck, aber er ist moderat."
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Optional additional cost */}
      <div className="space-y-2 pt-2">
        <Label htmlFor="additional-cost" className="text-sm font-medium text-foreground">
          Weitere Kosten <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <Textarea
          id="additional-cost"
          value={additionalCost}
          onChange={(e) => setAdditionalCost(e.target.value)}
          placeholder="z.B. Verzögerte Marktpositionierung erschwert späteres Pricing..."
          maxLength={200}
          rows={2}
          className="resize-none bg-background border-border focus:border-primary text-base leading-relaxed placeholder:text-muted-foreground/60"
        />
      </div>

      {/* Help collapsible */}
      <Collapsible className="mt-2">
        <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mx-auto">
          <HelpCircle className="w-4 h-4" />
          <span>Warum diese Bewertung?</span>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-3 animate-in slide-in-from-top-1 duration-200">
          <div className="p-4 bg-muted/50 border border-border rounded-xl max-w-lg mx-auto">
            <p className="text-sm text-foreground leading-relaxed">
              Diese Bewertung zeigt dir, wie <strong>dringend</strong> die Entscheidung ist. 
              Wenn in keinem Bereich hohe Kosten entstehen, ist Abwarten vielleicht die beste Option.
            </p>
            <div className="mt-3 pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground">
                <strong>Faustregel:</strong> Mindestens ein „Hoch" bedeutet: 
                Die Entscheidung verdient Priorität.
              </p>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
